import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

const alertStyles = cva('flex items-start gap-2.5 rounded-control border px-3 py-2.5 text-sm', {
  variants: {
    tone: {
      info: 'border-border bg-info-muted text-fg',
      success: 'border-border bg-success-muted text-fg',
      warning: 'border-border bg-warning-muted text-fg',
      danger: 'border-border bg-danger-muted text-fg',
    },
  },
  defaultVariants: { tone: 'info' },
});

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: ShieldAlert,
} as const;

export type AlertProps = ComponentProps<'div'> &
  VariantProps<typeof alertStyles> & {
    readonly title?: string | undefined;
  };

/**
 * Inline feedback for mutation results and query failures.
 *
 * `role="alert"` is only applied for danger/warning so that a routine success
 * message does not interrupt a screen reader mid-sentence.
 */
export function Alert({ className, tone = 'info', title, children, ...props }: AlertProps) {
  const resolvedTone = tone ?? 'info';
  const Icon = ICONS[resolvedTone];
  return (
    <div
      className={cn(alertStyles({ tone: resolvedTone }), className)}
      {...(resolvedTone === 'danger' || resolvedTone === 'warning' ? { role: 'alert' } : {})}
      {...props}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="flex flex-col gap-0.5">
        {title !== undefined && <p className="font-medium">{title}</p>}
        <div className="text-fg-muted [&_a]:underline">{children}</div>
      </div>
    </div>
  );
}
